import { useState, useEffect } from "react";
import { useParams, useNavigate, useSearchParams } from "react-router-dom";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { ArrowLeft, Loader2, Share2, Check, PackageOpen, MessageSquare } from "lucide-react";
import { api } from "@/lib/api";
import { X_HANDLE } from "@/config";
import { recordShare, trackShareEvent } from "@/lib/shareStats";
import { AuthImage } from "@/components/common/AuthImage";
import { ShareToXButton } from "@/components/common/ShareToX";
import { Button } from "@/components/ui/button";

const price = (c) => (c ? `$${(c / 100).toFixed(2)}` : "Free");

// Public-ish share target for a sticker pack (login-gated by ProtectedRoute). Creators share
// ${SHARE_BASE}/app/stickers/{packId}?ref=share to promote a pack; recipients land here to add/buy it.
export default function PackView() {
  const { packId } = useParams();
  const navigate = useNavigate();
  const qc = useQueryClient();
  const [searchParams] = useSearchParams();
  const [buying, setBuying] = useState(false);
  const { data: pack, isLoading, isError } = useQuery({
    queryKey: ["sticker-pack", packId],
    queryFn: () => api.stickerPack(packId),
    retry: false,
  });

  // Attribute a share-driven open (only when arriving via a shared link).
  useEffect(() => {
    if (searchParams.get("ref") === "share") trackShareEvent(packId, "open");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [packId]);

  // Per-pack tab title + OG/Twitter meta (client-side). NOTE: Discord/X/Telegram crawlers don't run JS —
  // this only helps in-app + JS-rendering contexts. True per-pack social cards need server/edge OG rendering
  // at the share URL (see HANDOFF_social_og_cards.md). og:image stays the public branded default.
  useEffect(() => {
    if (!pack?.title) return;
    const title = `${pack.title} — Trending on BLOQ Talk`;
    const desc = `${pack.sticker_count ? `${pack.sticker_count} stickers` : "Sticker pack"}${pack.author?.handle ? ` by @${pack.author.handle}` : ""} — add them on BLOQ Talk.`;
    const prevTitle = document.title;
    document.title = title;
    const setMeta = (selector, value) => {
      const el = document.head.querySelector(selector);
      if (!el) return () => {};
      const prev = el.getAttribute("content");
      el.setAttribute("content", value);
      return () => { if (prev != null) el.setAttribute("content", prev); };
    };
    const restorers = [
      setMeta('meta[property="og:title"]', title),
      setMeta('meta[name="twitter:title"]', title),
      setMeta('meta[property="og:description"]', desc),
      setMeta('meta[name="twitter:description"]', desc),
    ];
    return () => { document.title = prevTitle; restorers.forEach((r) => r()); };
  }, [pack]);

  const shareUrl = api.stickerShareUrl(packId);
  const share = async () => {
    recordShare(packId);
    try {
      if (navigator.share) { await navigator.share({ title: pack?.title || "BLOQ sticker pack", url: shareUrl }); return; }
      await navigator.clipboard.writeText(shareUrl);
      toast.success("Share link copied!");
    } catch { /* user dismissed share sheet */ }
  };

  const add = async () => {
    setBuying(true);
    try {
      const res = await api.stickerPackPurchaseCheckout(packId, `${window.location.origin}/app/stickers/${packId}`);
      if (res?.granted) { trackShareEvent(packId, "add"); toast.success("Added to your stickers!"); qc.invalidateQueries({ queryKey: ["sticker-pack", packId] }); return; }
      if (res?.checkout_url) { window.location.href = res.checkout_url; return; }
      toast.error("Couldn't start checkout.");
    } catch (e) {
      const detail = e?.response?.data?.detail;
      const code = typeof detail === "object" ? detail?.error : null;
      if (code === "already_owned") { toast.info("You already own this pack."); qc.invalidateQueries({ queryKey: ["sticker-pack", packId] }); }
      else if (code === "native_purchases_required" || e?.response?.status === 403) toast.info("Sticker packs are available in the BLOQ Talk app.");
      else toast.error((typeof detail === "object" ? detail?.message : detail) || "Couldn't add this pack.");
    } finally {
      setBuying(false);
    }
  };

  return (
    <div className="h-full overflow-y-auto bg-bloq-bg" data-testid="pack-view">
      <div className="mx-auto max-w-lg px-5 py-6">
        <button onClick={() => navigate("/app/chats")} className="mb-5 flex items-center gap-2 text-sm text-bloq-muted hover:text-white" data-testid="pack-back">
          <ArrowLeft className="h-4 w-4" /> Back to chats
        </button>

        {isLoading ? (
          <div className="flex justify-center py-20 text-bloq-muted"><Loader2 className="h-6 w-6 animate-spin" /></div>
        ) : isError || !pack ? (
          <div className="flex flex-col items-center gap-3 rounded-3xl border border-white/8 bg-bloq-s1 p-10 text-center" data-testid="pack-not-found">
            <PackageOpen className="h-8 w-8 text-bloq-muted" />
            <p className="text-sm text-bloq-muted">This sticker pack isn't available.</p>
            <Button onClick={() => navigate("/app/chats")} variant="outline" className="rounded-full border-white/15 bg-transparent text-white">Go to chats</Button>
          </div>
        ) : (
          <div className="space-y-5" data-testid="pack-detail">
            <div className="flex items-center gap-4 rounded-3xl border border-white/8 bg-bloq-s1 p-5">
              <div className="flex h-20 w-20 shrink-0 items-center justify-center overflow-hidden rounded-2xl bg-bloq-bg">
                {pack.cover_url
                  ? <AuthImage src={api.stickerCoverSrc(pack.cover_url)} alt="" className="h-full w-full object-contain" testid="pack-cover" />
                  : <PackageOpen className="h-8 w-8 text-bloq-muted" />}
              </div>
              <div className="min-w-0 flex-1">
                <h1 className="truncate font-heading text-xl font-bold text-white" data-testid="pack-title">{pack.title}</h1>
                <div className="text-sm text-bloq-muted" data-testid="pack-author">{pack.author?.handle ? `@${pack.author.handle}` : ""}{pack.sticker_count ? ` · ${pack.sticker_count} stickers` : ""}</div>
                <div className="mt-1 text-sm font-bold text-bloq-mint" data-testid="pack-price">{price(pack.price)}</div>
              </div>
              <button onClick={share} className="shrink-0 rounded-full border border-white/12 p-2.5 text-bloq-muted hover:text-white" title="Share this pack" data-testid="pack-share-btn">
                <Share2 className="h-4 w-4" />
              </button>
            </div>

            {pack.description && <p className="text-sm text-bloq-muted" data-testid="pack-description">{pack.description}</p>}

            {(pack.stickers || []).length > 0 && (
              <div className="grid grid-cols-4 gap-2 rounded-3xl border border-white/8 bg-bloq-s1 p-4" data-testid="pack-stickers-grid">
                {pack.stickers.map((s) => (
                  <div key={s.id} className="relative aspect-square rounded-xl border border-white/8 bg-bloq-s2 p-1.5">
                    <AuthImage src={api.stickerImageSrc(s.id)} alt={s.emoji || "sticker"} className="h-full w-full object-contain" />
                    {s.emoji && <span className="absolute bottom-0 right-0 text-xs">{s.emoji}</span>}
                  </div>
                ))}
              </div>
            )}

            {pack.owned ? (
              <div className="flex items-center justify-center gap-2 rounded-full border border-bloq-mint/25 bg-bloq-mint/[0.06] py-3 text-sm font-semibold text-bloq-mint" data-testid="pack-owned">
                <Check className="h-4 w-4" /> In your stickers — pick it from the sticker tray in any chat.
              </div>
            ) : (
              <Button onClick={add} disabled={buying} className="h-12 w-full rounded-full bloq-gradient font-bold text-bloq-bg hover:opacity-90" data-testid="pack-add-btn">
                {buying ? <Loader2 className="h-5 w-5 animate-spin" /> : <>{pack.price ? `Get pack · ${price(pack.price)}` : "Add to my stickers · Free"}</>}
              </Button>
            )}

            <ShareToXButton
              text={`I just dropped ${pack.title} on @${X_HANDLE} 🔥`}
              url={shareUrl}
              onShare={() => recordShare(packId)}
              testid="pack-share-x-btn"
              className="h-11 w-full border border-white/12 text-sm font-semibold text-white hover:border-bloq-mint/50"
            />

            <button onClick={() => navigate("/app/chats")} className="mx-auto flex items-center gap-1.5 text-xs text-bloq-muted hover:text-white" data-testid="pack-open-chats">
              <MessageSquare className="h-3.5 w-3.5" /> Open a chat to send a sticker
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
